import { makeStyles } from "@material-ui/styles";
import React, { useEffect } from "react";
import { useAppBridge, TitleBar } from "@shopify/app-bridge-react";
import Spinner from "../LoadingIndicator/Spinner";

export type Props = {
  loadPath?: string;
  redirectUri?: string;
  embedded?: boolean;
  installation?: boolean;
};

export default function ShopifyApp({ redirectUri, embedded, installation, loadPath }: Props) {
  const classes = useStyles();
  const shopify = useAppBridge(); // Initialize App Bridge instance

  useEffect(() => {
    // Loading screen Shopify lands on first: the initial top-level iframe GET can't carry an auth
    // header, so we wait for App Bridge (which then auto-attaches a fresh session token to
    // same-origin fetch/XHR) and move on to the real app. App Bridge can fail to initialise (CDN
    // blocked, not embedded, slow network), so we never wait forever: an 8s timeout falls through
    // to the app, a rejected `ready` is caught, and the navigation runs at most once.
    let done = false;
    let timer: ReturnType<typeof setTimeout>;

    const go = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);

      if (embedded && installation) {
        // App needs (re)installation / OAuth — break out of the iframe to the redirect URL.
        if (redirectUri) open(redirectUri, "_top");
        else if (loadPath) window.location.assign(loadPath);
        return;
      }

      if (loadPath) {
        // Prefer a Turbo Drive visit (keeps the App Bridge context alive); fall back to a full
        // navigation if Turbo isn't loaded.
        if (window.Turbo) window.Turbo.visit(loadPath);
        else window.location.assign(loadPath);
      }
    };

    timer = setTimeout(go, 8000);
    shopify?.ready?.then(go)?.catch(go);

    return () => {
      done = true;
      clearTimeout(timer);
    };
  }, [shopify]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes.root}>
      <TitleBar title="AirRobe Connector" />
      <Spinner width={54}/>
    </div>
  );
}

const useStyles = makeStyles({
  root: {
    position: "fixed",
    background: "#000",
    opacity: 0.5,
    top: 0,
    left: 0,
    height: "100vh",
    width: "100vw",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "$fadeIn30 1s normal forwards",
  },
  "@keyframes fadeIn30": {
    "0%": {
      opacity: "0%",
    },
    "100%": {
      opacity: "50%",
    },
  },
});
